import { useToast } from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useFetchUnpublishedProducts } from '@jane/business-admin/data-access';
import { useStoreDetails } from '@jane/business-admin/providers/StoreDetailsProvider';
import type { ProductTableType } from '@jane/business-admin/types';

import { ProductModal } from './ProductModal';
import { UnpublishedProductModal } from './unpublished/UnpublishedProductModal';

/**
 * @todo Ideally this doesn't exist and we load the product data within the modal,
 * but then we'll need loading states while there is no product data
 */
export const ModalDecider = () => {
  const { type = 'published' } = useParams<{
    type: keyof typeof ProductTableType;
  }>();
  const { product_id } = useParams<'product_id'>();

  const { storeId } = useStoreDetails();
  const navigate = useNavigate();

  const toast = useToast();

  const isUnpublished = useMemo(() => {
    return type === 'unpublished';
  }, [type]);

  const { data: unpublishedData, isPending: unpublishedLoading } =
    useFetchUnpublishedProducts({
      enabled: isUnpublished,
      params: {
        unmapped_product_id: product_id,
      },
      storeId,
    });

  const unpublishedProductData = useMemo(
    () =>
      unpublishedData?.pages?.flatMap((page) => page.unpublished_products) ??
      [],
    [JSON.stringify(unpublishedData)]
  );

  const isUnpublishedEmpty = useMemo(
    () => isEmpty(unpublishedProductData),
    [JSON.stringify(unpublishedProductData)]
  );

  if (
    (isUnpublished && unpublishedLoading) ||
    (isUnpublished && !unpublishedProductData)
  ) {
    return null;
  }

  if (isUnpublished && isUnpublishedEmpty) {
    toast.add({
      label: 'Product could not be found.',
      variant: 'error',
    });
    if (isUnpublished) {
      navigate(`/stores/${storeId}/products/published`);
    } else {
      navigate(`/stores/${storeId}/products/unpublished`);
    }
    return null;
  }

  if (isUnpublished) {
    return <UnpublishedProductModal product={unpublishedProductData[0]} />;
  }
  return <ProductModal tableType={type} />;
};
